import React, { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { Grid, FormControlLabel, Checkbox, TextField, Typography } from '@mui/material';
import { useMutation } from 'react-query';
import { API } from 'core/requests';
import { useCompany } from 'contexts'
import CardSection from './CardSection';
import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { MoneyInput } from 'components';

export default function CheckoutForm({ handleClose, setWalletMoney, amount, getCurrentTransaction, refetchCustomerCardDetails, refreshTableData, selectedCardId, setSelectedCardId }: {
  handleClose: any;
  setWalletMoney: any;
  amount: any;
  selectedCardId: any;
  getCurrentTransaction: () => void;
  refetchCustomerCardDetails: () => void;
  refreshTableData: () => void;
  setSelectedCardId: (any) => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { company } = useCompany()!

  const stripe = useStripe();
  const elements = useElements();

  const com_id: any = company?.id;
  const credit_card_fee_type: any = company?.credit_card_fee_type;
  const credit_card_fee: any = company?.credit_card_fee;

  let totalAmount = parseFloat(amount);
  let fee = 0;
  if (credit_card_fee_type === 'percentage') {
    fee = (parseFloat(amount) * credit_card_fee) / 100;
    totalAmount = parseFloat(amount) + fee;
  } else if (credit_card_fee_type === 'fixed') {
    fee = credit_card_fee;
    totalAmount = parseFloat(amount) + credit_card_fee;
  } 

  const [feeType, setFeeType] = useState<any>(credit_card_fee_type);
  const [feeValue, setFeeValue] = useState<number>(credit_card_fee);
  const [baseAmount, setBaseAmount] = useState<any>(amount);
  const [feeAmount, setFeeAmount] = useState<number>(fee);
  const [totalWalletMoney, setTotalWalletMoney] = useState<number>(totalAmount);
  const [saveCustomerCard, setSaveCustomerCard] = useState<any>(true);
  const [loading, setLoading] = useState<boolean>(false);

  let payloadTmp:any = {};
  const { mutate: addmoneyCard } = useMutation((payload: any) => {
    payloadTmp = payload;
    return API.createOrUpdate('addMoneyWithCart', payload);
  }, {
      onSuccess: (response: any) => {
        if (!response.errors) {
          if(payloadTmp.save_card){
						enqueueSnackbar('Credit Card added successfully.',
							{
								variant: 'success',
							},
						);
					}
          enqueueSnackbar(response?.message,
						{
							variant: response?.status === 'succeeded' ? 'success' : 'error',
						},
					);
          setWalletMoney(1.0);
          refetchCustomerCardDetails();
          getCurrentTransaction();
          refreshTableData();
          handleClose();
          setLoading(false);
        } else {
          setWalletMoney(1.0);
          refetchCustomerCardDetails();
          getCurrentTransaction();
          refreshTableData();
          handleClose();
          setLoading(false);
        }
      },
    },
  )

  const { mutate: addFundWithSavedCard } = useMutation(
    (payload: any) => API.create('addFundWithSavedCard', payload), 
    {
      onSuccess: (response: any) => {
        if (!response?.errors) {
          enqueueSnackbar(response?.message,
						{
							variant: response?.status === 'succeeded' ? 'success' : 'error',
						},
					);

          setWalletMoney(1.0);
          setBaseAmount(1.0);
          setFeeAmount(0)
          refetchCustomerCardDetails();
          getCurrentTransaction();
          refreshTableData();
          setSelectedCardId(null);
          setLoading(false);
          handleClose();
        };
      },
    }
  )

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSubmit = async (event) => {

    if(!selectedCardId) {
      if (!stripe || !elements) {
        return;
      }

      const card: any = elements.getElement(CardElement);
      const result = await stripe.createToken(card);

      if (result.error) {
        // Show error to your customer.
        enqueueSnackbar(result?.error.message,
          { variant: 'error' },
        );
      } else {
        const cartData = {
          fee_type: feeType,
          fee_value: feeValue,
          total_amount: parseFloat(totalWalletMoney.toFixed(2)),
          fee_amount: feeAmount.toFixed(2), 
          amount: parseFloat(baseAmount).toFixed(2),
          currency: "usd",
          company_id: com_id,
          token: result.token?.id,
          stripe_data: result.token,
          save_card: saveCustomerCard,
        }
        setLoading(true);
        addmoneyCard(cartData)
      }
    } else {
      const cartData = {
        card_id: selectedCardId,
        fee_type: feeType,
        fee_value: feeValue,
        total_amount: parseFloat(totalWalletMoney.toFixed(2)),
        fee_amount: feeAmount.toFixed(2), 
        amount: parseFloat(baseAmount).toFixed(2),
        currency: "usd",
        company_id: com_id,
        save_card: true,
      }
      setLoading(true);
      addFundWithSavedCard(cartData)
    }
   
  };

  return (
    <>
      {
        !selectedCardId && (
          <CardSection />
        )
      }
      <Grid container spacing={0}>
        <Grid item xs={12}>
          <Box display={'flex'} alignItems={'center'} mt={4}>
            <TextField
              data-cy='amount'
              id='amount'
              name='amount'
              label={'Reward Deposit Amount'}
              placeholder="Enter Amount"
              variant='outlined'
              value={parseFloat(amount)}
              required
              onChange={(event: any) => {
                setWalletMoney(parseFloat(event.target.value).toFixed(2));
                if (credit_card_fee_type === 'percentage') {
                  const fee = (parseFloat(event.target.value) * credit_card_fee) / 100;
                  const total = parseFloat(event.target.value) + fee;

                  setFeeType(credit_card_fee_type);
                  setFeeValue(credit_card_fee || 0);
                  setBaseAmount(parseFloat(event.target.value) || 0);
                  setFeeAmount(fee || 0);
                  setTotalWalletMoney(total || 0);
                } else if (credit_card_fee_type === 'fixed') {
                  const total = parseFloat(event.target.value) + credit_card_fee;

                  setFeeType(credit_card_fee_type);
                  setFeeValue(credit_card_fee || 0);
                  setBaseAmount(parseFloat(event.target.value) || 0);
                  setFeeAmount(credit_card_fee || 0);
                  setTotalWalletMoney(total || 0);
                } else {
                  setFeeType(credit_card_fee_type);
                  setFeeValue(credit_card_fee || 0);
                  setBaseAmount(parseFloat(event.target.value) || 0);
                  setFeeAmount(credit_card_fee || 0);
                  setTotalWalletMoney(parseFloat(event.target.value) || 0);
                }
              }}
              fullWidth
              margin='normal'
              InputProps={{
                inputComponent: MoneyInput,
              }}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{marginTop: 0}}
            />
          </Box>
          {
            !selectedCardId && (
              <Box>
                <FormControlLabel
                  key={'allowSaveCard'}
                  control={
                    <Checkbox
                      data-cy='allowSaveCard'
                      name='allowSaveCard'
                      onChange={() => setSaveCustomerCard(!saveCustomerCard)}
                      checked={saveCustomerCard}
                    />
                  }
                  label="Save Credit Card"
                />
              </Box>
            )
          }
        </Grid>
        <Grid item xs={12}>
          <Grid container mt={3}>
            <Grid item xs={5}>
              <Typography variant='h6' component='h6' fontWeight={500} textAlign={"end"} >
                Transaction Fee:
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography variant='h6' color={'#000000'} component='h6' fontWeight={600} textAlign={"end"} >
                {
                  credit_card_fee_type === 'percentage' 
                  ? `${credit_card_fee}%` 
                  : credit_card_fee_type === 'fixed' 
                    ? `$${credit_card_fee.toFixed(2)}` 
                    : '$0.00'
                } 
              </Typography>
            </Grid>

            <Grid item xs={5}>
              <Typography variant='h6' component='h6' fontWeight={500} textAlign={"end"} >
                Transaction Total:
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography variant='h6' color={'#000000'} component='h6' fontWeight={600} textAlign={"end"}>
                {`$${totalWalletMoney.toFixed(2)}`}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Box mt={3}>
        <LoadingButton
          type="button"
          loading={loading}
          autoFocus
          fullWidth
          variant='contained'
          color={'primary'}
          onClick={(e) => handleSubmit(e)}
        >
          <span>Pay</span>
        </LoadingButton>
      </Box>
    </>
  );
}